import React, { useMemo } from 'react';
import { Select } from 'antd';
import Table from 'components/Table';
import MetricLabel from 'components/MetricLabel';
import MetricTooltip from 'components/MetricTooltip';
import SegmentTag from 'components/SegmentTag';
import DateRangeTag from 'components/DateRangeTag';
import MetricValue from 'components/MetricValue';

import DimensionValue from 'components/DimensionValue';
import DimensionLabel from 'components/DimensionLabel';
import CenterContent from 'components/CenterContent';
import { findTotals, getSortOrder, getTableData, getTotals } from './utils';

function getSegmentsColumnHeader({ segments, level = 0, getTitle, totals, ...props }) {
  if (!totals) {
    return undefined;
  }
  if (segments.length > level) {
    const segment = segments[level];
    return [{
      title: getTitle(segment, level),
      level,
      segment,
      ...props,
      children: segments.length > level + 1 ? getSegmentsColumnHeader({ segments, level: level + 1, getTitle, totals, ...props }) : null,
    }];
  }

  return [];
}

function getMetricColumn({ metric, dimensions, segments, totals, showChangeOverTime, shareOfTotal }) {
  const type = metric.type || 'number';

  const getTitle = (s) => {
    const result = findTotals({ totals, segmentId: s.id });
    return result?.[metric.name] != null && (
      <MetricTooltip
        metric={metric}
        dimensions={dimensions}
        row={totals}
      >
        <span>
          <MetricValue metric={metric} row={result} showChange={showChangeOverTime} showShareOfTotal={shareOfTotal} total={totals?.[metric.name]} />
        </span>
      </MetricTooltip>
    );
  };

  const render = (_, row) => (
    <MetricTooltip
      metric={metric}
      dimensions={dimensions}
      row={row}
    >
      <span>
        <MetricValue metric={metric} row={row} showChange={showChangeOverTime} showShareOfTotal={shareOfTotal} total={findTotals({ totals, segmentId: row.segmentId })?.[metric.name]} />
      </span>
    </MetricTooltip>
  );

  const children = getSegmentsColumnHeader({
    totals,
    segments,
    dataIndex: metric.name,
    type,
    getTitle,
    render,
    width: metric.width,
    align: 'right',
  });

  return {
    title: <MetricLabel {...metric} showTooltip />,
    dataIndex: metric.name,
    type,
    align: children?.length > 1 ? 'center' : 'right',
    children,
    defaultSortOrder: metric.sortPriority === 1 ? getSortOrder(metric.sortOrder) : null,
    render,
    width: metric.width,
  };
}

const renderDimension = (dimension) => (value, row) => <DimensionValue dimension={dimension} value={value} row={row} />;
const renderSegment = (segment) => <SegmentTag segment={segment} />;
const renderDateRange = (dateRange) => <DateRangeTag dateRange={dateRange} short />;

const DimensionHeaderSelect = ({ dimension, switchableDimensions, activeDimension, onActiveDimensionChange }) => {
  if (!switchableDimensions?.length || switchableDimensions.length <= 1) {
    return <DimensionLabel {...dimension} showTooltip />;
  }

  return (
    <span className="table-dimension-header-select-wrap" onClick={e => e.stopPropagation()}>
      <Select
        className="table-dimension-header-select"
        value={activeDimension}
        onChange={onActiveDimensionChange}
        popupMatchSelectWidth={false}
        options={switchableDimensions.map(d => ({
          label: <DimensionLabel {...d} lineBreaks={false} />,
          value: d.name,
        }))}
      />
    </span>
  );
};

const getColumns = ({
  mainSegment,
  comparisonSegments,
  dimensions,
  metrics,
  totals,
  dateComparisonsAsSegments,
  segments,
  showChangeOverTime,
  shareOfTotal,
  switchableDimensions,
  activeDimension,
  onActiveDimensionChange,
}) => {
  const columns = [];

  columns.push(...dimensions.filter(d => d.hidden !== true).map((d, index) => ({
    title: (
      <DimensionHeaderSelect
        dimension={d}
        switchableDimensions={index === 0 ? switchableDimensions : undefined}
        activeDimension={activeDimension}
        onActiveDimensionChange={onActiveDimensionChange}
      />
    ),
    dataIndex: d.name,
    defaultSortOrder: d.sortPriority === 1 ? getSortOrder(d.sortOrder) : null,
    children: getSegmentsColumnHeader({
      totals,
      getTitle: (s, level) => (level === 0 ? 'Totals' : null),
      dataIndex: d.name,
      segments,
      render: renderDimension(d),
      onCell: (row) => ({
        rowSpan: row?.primaryData ? 0 : segments.length,
      }),
      onHeaderCell: (col) => ({
        rowSpan: col.level === 0 ? segments.length : 0,
      }),
      type: d.type,
      width: d.width,
    }),
    type: d.type,
    render: renderDimension(d),
    width: d.width,
  })));

  if (comparisonSegments?.length > 0) {
    columns.push({
      title: 'Segment',
      dataIndex: 'segment',
      children: getSegmentsColumnHeader({
        totals,
        dataIndex: 'segment',
        segments,
        getTitle: renderSegment,
        render: renderSegment,
        onCell: (row) => (dateComparisonsAsSegments ? {
          rowSpan: row.segment?.dateRangeSegments ? row.segment.dateRangeSegments.length + 1 : 0,
        } : {}),
        onHeaderCell: (col) => (dateComparisonsAsSegments ? {
          rowSpan: col.segment?.dateRangeSegments ? col.segment.dateRangeSegments.length + 1 : 0,
        } : {}),
      }),
      render: renderSegment,
    });
  }

  if ((dateComparisonsAsSegments && mainSegment?.dateRangeSegments?.length > 0) || comparisonSegments?.some(s => s.customDateRange)) {
    columns.push({
      title: 'Period',
      dataIndex: ['segment', 'dateRange'],
      children: getSegmentsColumnHeader({
        totals,
        dataIndex: ['segment', 'dateRange'],
        segments,
        getTitle: (s) => renderDateRange(s.dateRange),
        render: renderDateRange,
      }),
      render: renderDateRange,
    });
  }

  if (metrics.length > 0) {
    columns.push(...metrics.map(metric => getMetricColumn({ metric, dimensions, segments, totals, showChangeOverTime, shareOfTotal })));
  }

  return columns;
};

const TableResult = ({
  results,
  config,
  segments,
  mainSegment,
  comparisonSegments,
  switchableDimensions,
  activeDimension,
  onActiveDimensionChange,
}) => {
  const { dimensions = [], metrics = [], settings } = config || {};
  const { dateComparisonsAsSegments, showChangeOverTime, shareOfTotal } = settings || {};

  const tableData = useMemo(() => getTableData({ dimensions, mainSegment, comparisonSegments, results, settings }), [results, dimensions, mainSegment, comparisonSegments, settings]);
  const totals = useMemo(() => (tableData.length > 1 ? getTotals({ mainSegment, comparisonSegments, results }) : null), [mainSegment, comparisonSegments, results, tableData.length]);

  const columns = useMemo(() => getColumns({
    dimensions,
    metrics,
    mainSegment,
    comparisonSegments,
    totals,
    segments,
    dateComparisonsAsSegments,
    showChangeOverTime: showChangeOverTime && mainSegment?.dateRangeSegments?.length > 0,
    shareOfTotal,
    switchableDimensions,
    activeDimension,
    onActiveDimensionChange,
  }), [dimensions, metrics, mainSegment, comparisonSegments, totals, segments, dateComparisonsAsSegments, showChangeOverTime, shareOfTotal, switchableDimensions, activeDimension, onActiveDimensionChange]);

  return (
    <>
      <CenterContent horizontal wrap style={{ padding: '8px 0' }}>
        {segments.map(s => <SegmentTag key={s.id} segment={s} showDateRange />)}
      </CenterContent>
      <Table
        className="table-result"
        interactive={false}
        columns={columns}
        dataSource={tableData}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: tableData?.map(r => r.id),
        }}
        scroll={{
          x: 'max-content',
        }}
        showSorterTooltip={false}
        pagination={config?.settings?.pageLimit ? {
          hideOnSinglePage: true,
          defaultPageSize: config?.settings?.pageLimit,
          pageSizeOptions: [5, 10, 50, 100],
        } : false}
      />
    </>
  );
};

export default TableResult;
